import { booleanToTextValue } from "~/app/utils/helpers"
import { notNullish } from "~/app/utils/typeUtils"
import { TOptions } from "i18next"
import type { TFunction } from "react-i18next"
import {
  CalendarDurationType,
  PostTerminationPaymentAmountType,
  PostTerminationPaymentFrequency,
  PostTerminationRestrictedArea,
} from "@oysterhr/schema"
import {
  DurationOption,
  NonSolicitationOptionsEnum,
  RestrictiveCovenantsFormValues,
  StringifiedBoolean,
} from "../types"
import { timeWithUnit } from "@oysterhr/advisor"
import { parseRestrictedAreas } from "./parseRules"
import { compact } from "lodash-es"
import { RestrictiveCovenantsRulesFragment } from "~/gql/graphql"

const withDefaultNS = (t: TFunction, ns: string) => (key: string, options?: TOptions) =>
  t(key, { ns, ...options })

export const getDurationOptions = (
  t: TFunction,
  knowledgeBase?: RestrictiveCovenantsRulesFragment,
): {
  label: string
  value: DurationOption
}[] => {
  const translate = withDefaultNS(t, "company_interface/CompanyNewHireFTEPage")

  const defaultSuggestions = [
    {
      label: `6 ${translate("restrictiveCovenants.duration.general.MONTHS", {
        count: 6,
      })}`,
      value: `6_${CalendarDurationType.Months}` as DurationOption,
    },
    {
      label: `12 ${translate("restrictiveCovenants.duration.general.MONTHS", {
        count: 12,
      })}`,
      value: `12_${CalendarDurationType.Months}` as DurationOption,
    },
  ]

  const knowledgeBaseSuggestions = knowledgeBase?.postTermination?.suggestedDurations?.map(item => {
    return {
      label: `${item.value} ${translate("restrictiveCovenants.duration.general.MONTHS", {
        count: item.value || 0,
      })}`,
      value: `${item.value}_${CalendarDurationType.Months}` as DurationOption,
    }
  })

  const suggestions = knowledgeBaseSuggestions || defaultSuggestions

  const customMax = knowledgeBase?.postTermination?.maxDuration
    ? translate("restrictiveCovenants.duration.fields.durationOption.createCustomMax", {
        maxPeriod: timeWithUnit(t, knowledgeBase?.postTermination?.maxDuration),
      })
    : ""
  return [
    ...suggestions,
    {
      label:
        translate("restrictiveCovenants.duration.fields.durationOption.createCustom") + customMax,
      value: "custom",
    },
  ]
}

export const getPaymentTypeOptions = (t: TFunction) => {
  const translate = withDefaultNS(t, "company_interface/CompanyNewHireFTEPage")
  return [
    {
      label: translate("restrictiveCovenants.paymentType.fields.amountType.options.fixed"),
      value: PostTerminationPaymentAmountType.Flat,
    },
    {
      label: translate("restrictiveCovenants.paymentType.fields.amountType.options.monthly"),
      value: PostTerminationPaymentAmountType.PercentageMonthlyGross,
    },
  ]
}

export const getPaymentFrequencyOptions = (t: TFunction) => {
  const translate = withDefaultNS(t, "company_interface/CompanyNewHireFTEPage")
  return [
    {
      label: translate(
        "restrictiveCovenants.paymentType.fields.paymentFrequency.options.onTermination",
      ),
      value: PostTerminationPaymentFrequency.OnTermination,
    },
    {
      label: translate(
        "restrictiveCovenants.paymentType.fields.paymentFrequency.options.monthlyPostTermination",
      ),
      value: PostTerminationPaymentFrequency.MonthlyPostTermination,
    },
  ]
}

export const getNonSolicitionsOptions = (
  t: TFunction,
  nonSolicitationCustomers = true,
  nonSolicitationEmployees = true,
) => {
  const translate = withDefaultNS(t, "company_interface/CompanyNewHireFTEPage")
  return compact([
    nonSolicitationEmployees && {
      label: translate(
        `restrictiveCovenants.postTerminationClauses.fields.nonSolicitationOptions.${NonSolicitationOptionsEnum.Employees}.label`,
      ),
      description: translate(
        `restrictiveCovenants.postTerminationClauses.fields.nonSolicitationOptions.${NonSolicitationOptionsEnum.Employees}.description`,
      ),
      value: NonSolicitationOptionsEnum.Employees,
    },
    nonSolicitationCustomers && {
      label: translate(
        `restrictiveCovenants.postTerminationClauses.fields.nonSolicitationOptions.${NonSolicitationOptionsEnum.Customers}.label`,
      ),
      description: translate(
        `restrictiveCovenants.postTerminationClauses.fields.nonSolicitationOptions.${NonSolicitationOptionsEnum.Customers}.description`,
      ),
      value: NonSolicitationOptionsEnum.Customers,
    },
    nonSolicitationEmployees &&
      nonSolicitationCustomers && {
        label: translate(
          `restrictiveCovenants.postTerminationClauses.fields.nonSolicitationOptions.${NonSolicitationOptionsEnum.Both}.label`,
        ),
        description: translate(
          `restrictiveCovenants.postTerminationClauses.fields.nonSolicitationOptions.${NonSolicitationOptionsEnum.Both}.description`,
        ),
        value: NonSolicitationOptionsEnum.Both,
      },
  ])
}

export const getBooleanTextOptions = (t: TFunction, currentValue?: StringifiedBoolean) => {
  const translate = withDefaultNS(t, "company_interface/CompanyNewHireFTEPage")
  return [
    {
      value: booleanToTextValue(null),
      label: translate("label.null"),
      disabled: booleanToTextValue(currentValue) !== "null",
    },
    { value: booleanToTextValue(true), label: translate("label.yes") },
    { value: booleanToTextValue(false), label: translate("label.no") },
  ].filter(notNullish)
}

export const getRestrictedAreasOptions = (
  t: TFunction,
  currentValues?: RestrictiveCovenantsFormValues,
  knowledgeBase?: RestrictiveCovenantsRulesFragment,
) => {
  const translate = withDefaultNS(t, "company_interface/CompanyNewHireFTEPage")

  const defaultValues = [
    {
      label: translate("restrictiveCovenants.restrictedAreas.options.world.label"),
      description: translate("restrictiveCovenants.restrictedAreas.options.world.description"),
      value: PostTerminationRestrictedArea.World,
    },
    {
      label: translate("restrictiveCovenants.restrictedAreas.options.country.label"),
      description: translate("restrictiveCovenants.restrictedAreas.options.country.description"),
      value: PostTerminationRestrictedArea.Country,
    },
    {
      label: translate("restrictiveCovenants.restrictedAreas.options.subdivision.label"),
      description: translate(
        "restrictiveCovenants.restrictedAreas.options.subdivision.description",
      ),
      value: PostTerminationRestrictedArea.AdministrativeDivision,
    },
    {
      label: translate("restrictiveCovenants.restrictedAreas.options.city.label"),
      description: translate("restrictiveCovenants.restrictedAreas.options.city.description"),
      value: PostTerminationRestrictedArea.City,
    },
  ]

  if (!knowledgeBase?.postTermination) return defaultValues

  const { validRestrictedAreas, isApplicable } = parseRestrictedAreas({
    currentValues,
    knowledgeBase,
  })

  if (!isApplicable) return []

  return defaultValues.filter(item => validRestrictedAreas.includes(item.value))
}
